// src/pages/PlanPremium.jsx

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  BrainCircuit,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import "../sass/pages/plan-premium.scss";
import Logo from "../assets/logoClaro.webp";

const PLAN = {
  id: "premium",
  name: "PsyAssist Premium",
  price: "R$ 69,97",
  pricePayload: "R$ 69,97/MÊS",
  normalPrice: "R$ 99,97",
  normalPricePayload: "R$ 99,97/MÊS",
  priceId:
    process.env.REACT_APP_STRIPE_PRICE_PREMIUM ||
    "price_1U18OVKKOfm7LsUJnbIcjwgc",
  promotionalOffer: true,
  promotionalLabel:
    "Preço promocional de lançamento",
  trialDays: 7,
};

const FEATURES = [
  "Tudo do Plano Essencial",
  "PSYA, sua agente inteligente",
  "Insights financeiros e operacionais",
  "Memória confirmada da PSYA",
  "Integração com Google Agenda",
  "Criação de salas no Google Meet",
  "Lembretes configuráveis por e-mail",
  "Régua de cobrança automática por e-mail",
  "Dashboard e indicadores avançados",
  "Suporte prioritário",
  "WhatsApp em implementação",
];

const SELECTED_PLAN_STORAGE_KEY =
  "psyassist:selected-plan";

const PlanPremium = () => {
  const navigate = useNavigate();

  const handleSubscribe = () => {
    if (!PLAN.id || !PLAN.priceId) {
      console.error(
        "Plano Premium inválido ou sem priceId.",
        PLAN
      );

      return;
    }

    const payload = {
      planId: PLAN.id,

      planName: PLAN.name,

      planPriceIndividual:
        PLAN.pricePayload,

      planPriceClinica:
        PLAN.pricePayload,

      planNormalPriceIndividual:
        PLAN.normalPricePayload,

      planNormalPriceClinica:
        PLAN.normalPricePayload,

      priceId: PLAN.priceId,

      tipoUsuario: "individual",

      promotionalOffer:
        PLAN.promotionalOffer === true,

      promotionalLabel:
        PLAN.promotionalLabel,

      trialDays: PLAN.trialDays,

      selectedAt:
        new Date().toISOString(),
    };

    try {
      sessionStorage.setItem(
        SELECTED_PLAN_STORAGE_KEY,
        JSON.stringify(payload)
      );

      sessionStorage.setItem(
        "selectedPlan",
        JSON.stringify(payload)
      );
    } catch (error) {
      console.error(
        "Não foi possível salvar o plano Premium na sessão:",
        error
      );
    }

    console.log(
      `Plano selecionado: ${PLAN.name}`,
      payload
    );

    navigate(
      "/termos",
      {
        state: payload,
      }
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <main className="plan-detail plan-detail--premium">
      <div
        className="plan-detail__background"
        aria-hidden="true"
      >
        <span className="plan-detail__orb plan-detail__orb--one" />
        <span className="plan-detail__orb plan-detail__orb--two" />
        <span className="plan-detail__grid" />
      </div>

      <div className="plan-detail__shell">
        <aside className="plan-detail__summary">
          <div className="plan-detail__brand">
            <img
              src={Logo}
              alt="PsyAssist Pro"
            />

            <div>
              <strong>
                PsyAssist Pro
              </strong>

              <span>
                Inteligência para sua rotina profissional
              </span>
            </div>
          </div>

          <div className="plan-detail__summary-content">
            <span className="plan-detail__kicker">
              <Sparkles size={15} />
              Plano Premium
            </span>

            <h1>
              Automatize sua operação e trabalhe com mais contexto.
            </h1>

            <p>
              Integre agenda, atendimentos online, lembretes, cobranças e a
              PSYA em uma experiência mais completa para sua prática.
            </p>

            <div className="plan-detail__price">
              <span>
                Preço promocional de lançamento
              </span>

              <strong>
                {PLAN.price}
                <small>/mês</small>
              </strong>

              <p>
                Depois do lançamento:{" "}
                <b>
                  {PLAN.normalPrice}/mês
                </b>
              </p>
            </div>
          </div>

          <div className="plan-detail__aside-note">
            <BrainCircuit size={19} />

            <div>
              <strong>
                PSYA integrada ao Premium
              </strong>

              <span>
                Insights de apoio à gestão sem substituir julgamento ou
                responsabilidade profissional.
              </span>
            </div>
          </div>
        </aside>

        <section className="plan-detail__panel">
          <header className="plan-detail__panel-head">
            <div className="plan-detail__panel-icon">
              <BrainCircuit size={22} />
            </div>

            <div>
              <span>
                Profissional individual
              </span>

              <h2>
                PsyAssist Premium
              </h2>

              <p>
                Gestão avançada com integrações, automações e a agente
                inteligente PSYA.
              </p>
            </div>
          </header>

          <section className="plan-detail__trial">
            <div className="plan-detail__trial-icon">
              <Sparkles size={19} />
            </div>

            <div>
              <span>
                Período de avaliação
              </span>

              <strong>
                7 dias grátis
              </strong>

              <p>
                O cartão é solicitado no cadastro, mas a cobrança mensal só
                começa após o período de teste se a assinatura for mantida.
              </p>
            </div>
          </section>

          <section className="plan-detail__features">
            <div className="plan-detail__section-title">
              <span>
                Recursos incluídos
              </span>

              <strong>
                Tudo do Essencial, mais automação e inteligência
              </strong>
            </div>

            <ul>
              {FEATURES.map((feature) => (
                <li key={feature}>
                  <CheckCircle2
                    size={17}
                    aria-hidden="true"
                  />

                  <span>
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="plan-detail__checkout-info">
            <article>
              <CalendarDays size={18} />

              <div>
                <span>
                  Primeiro ciclo
                </span>

                <strong>
                  7 dias sem cobrança
                </strong>
              </div>
            </article>

            <article>
              <CreditCard size={18} />

              <div>
                <span>
                  Após o teste
                </span>

                <strong>
                  {PLAN.price}/mês
                </strong>
              </div>
            </article>

            <article>
              <BadgeCheck size={18} />

              <div>
                <span>
                  Experiência
                </span>

                <strong>
                  Premium + PSYA
                </strong>
              </div>
            </article>
          </section>

          <div className="plan-detail__actions">
            <button
              type="button"
              className="plan-detail__primary"
              onClick={handleSubscribe}
            >
              Começar com o Premium
              <ArrowRight size={18} />
            </button>

            <button
              type="button"
              className="plan-detail__secondary"
              onClick={handleBack}
            >
              <ArrowLeft size={17} />
              Voltar
            </button>
          </div>

          <footer className="plan-detail__footnote">
            <ShieldCheck size={15} />

            <p>
              Os recursos de IA apoiam a gestão e o trabalho profissional,
              sem substituir avaliação, conduta ou responsabilidade técnica.
            </p>
          </footer>
        </section>
      </div>
    </main>
  );
};

export default PlanPremium;
